"use client";
import React from "react";
import { Caveat } from "next/font/google";
import { motion } from "framer-motion";

const caveat = Caveat({ subsets: ["latin"], weight: ["400", "700"], display: "swap" });

export default function EyewearInventorySection({ section }) {
  const title = section?.title || "Inventory management at your fingertips";
  const items = section?.items || [
    {
      bold: "Real-time stock tracking.",
      text: " Monitor inventory levels closely to avoid stockouts and overstocking."
    },
    {
      bold: "Automated reordering.",
      text: " Set up automated purchase orders to ensure timely replenishment. Replenish from either vendor or another warehouse!"
    },
    {
      bold: "Lots and serial numbers.",
      text: " Quickly locate products using serial numbers, lots, codes, or categories."
    }
  ];

  return (
    <section className="py-20 lg:py-24 px-6 sm:px-12 lg:px-24 relative overflow-visible bg-transparent font-sans text-white w-full z-10">
      <div className="container mx-auto max-w-[1400px]">

        {/* Title Section */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className={`text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white inline-flex flex-wrap justify-center items-center gap-3 ${caveat.className}`}>
            <span>Inventory management at your</span>
            <span className="relative inline-block pb-2 text-white">
              fingertips
              <svg 
                className="absolute bottom-0 left-0 w-full h-3 text-amber-400 overflow-visible" 
                viewBox="0 0 100 20" 
                fill="none" 
                preserveAspectRatio="none"
              >
                <path 
                  d="M 5 12 Q 25 18, 50 12 T 95 14" 
                  stroke="currentColor" 
                  strokeWidth="5" 
                  strokeDasharray="6 4"
                  strokeLinecap="round"
                />
              </svg>
            </span>
          </h2>
        </motion.div>

        {/* 2 Column Grid */}
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          
          {/* Left Column: Feature Text List */}
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5 space-y-8"
          >
            <div className="space-y-6 text-slate-300 text-lg md:text-xl leading-relaxed">
              {items.map((item, idx) => (
                <p key={idx}>
                  <strong className="text-white font-semibold">{item.bold}</strong>
                  {item.text}
                </p>
              ))}
            </div>
          </motion.div>

          {/* Right Column: Card 1 (Inventory Overview) + Card 2 (Overlapping Stock Details) - NO Giant Outer Container Box */}
          <motion.div 
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7 relative pb-12 sm:pb-16"
          >
            {/* Card 1: Main Inventory Overview Dashboard Card */}
            <div className="bg-white rounded-2xl p-5 sm:p-6 shadow-2xl border border-slate-100 text-slate-800 font-sans max-w-2xl relative transition-transform duration-300 hover:scale-[1.01]">
              {/* Odoo Navbar */}
              <div className="flex items-center justify-between border-b border-slate-100 pb-3 mb-4 text-xs font-semibold text-slate-600">
                <div className="flex items-center gap-2">
                  <span className="text-purple-700 font-bold text-base">odoo</span>
                  <span className="text-slate-300">|</span>
                  <span className="text-slate-800 font-medium">Inventory</span>
                </div>
                <div className="flex items-center gap-3 text-[11px]">
                  <span className="bg-slate-100 text-slate-800 px-2 py-1 rounded font-semibold">Today</span>
                  <span className="text-slate-400">This Week</span>
                  <span className="text-slate-400 hidden sm:inline">All Warehouses</span>
                </div>
              </div>

              {/* Dashboard Content */}
              <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-3">Inventory Overview</h3>

              <div className="grid grid-cols-12 gap-4 items-end">
                {/* Bar Chart Graphics */}
                <div className="col-span-12 sm:col-span-8 bg-slate-50 rounded-xl p-3.5 border border-slate-100">
                  <div className="flex items-center justify-between text-[11px] font-semibold mb-3 text-slate-500">
                    <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-teal-500"></span> Incoming</span>
                    <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-pink-500"></span> Outgoing</span>
                    <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-orange-400"></span> Internal</span>
                  </div>

                  {/* Dynamic SVG Bar Chart */}
                  <div className="h-40 w-full flex items-end justify-between gap-2 pt-2 px-1">
                    {/* Jan */}
                    <div className="flex flex-col items-center flex-1 h-full justify-end gap-1">
                      <div className="w-full flex items-end gap-0.5 h-32">
                        <div className="w-1/3 bg-teal-500 h-[60%] rounded-t"></div>
                        <div className="w-1/3 bg-pink-500 h-[40%] rounded-t"></div>
                        <div className="w-1/3 bg-orange-400 h-[20%] rounded-t"></div>
                      </div>
                      <span className="text-[10px] text-slate-400">Jan</span>
                    </div>
                    {/* Feb */}
                    <div className="flex flex-col items-center flex-1 h-full justify-end gap-1">
                      <div className="w-full flex items-end gap-0.5 h-32">
                        <div className="w-1/3 bg-teal-500 h-[85%] rounded-t"></div>
                        <div className="w-1/3 bg-pink-500 h-[50%] rounded-t"></div>
                        <div className="w-1/3 bg-orange-400 h-[30%] rounded-t"></div>
                      </div>
                      <span className="text-[10px] text-slate-400">Feb</span>
                    </div>
                    {/* Mar */}
                    <div className="flex flex-col items-center flex-1 h-full justify-end gap-1">
                      <div className="w-full flex items-end gap-0.5 h-32">
                        <div className="w-1/3 bg-teal-500 h-[45%] rounded-t"></div>
                        <div className="w-1/3 bg-pink-500 h-[95%] rounded-t"></div>
                        <div className="w-1/3 bg-orange-400 h-[40%] rounded-t"></div>
                      </div>
                      <span className="text-[10px] text-slate-400">Mar</span>
                    </div>
                    {/* Apr */}
                    <div className="flex flex-col items-center flex-1 h-full justify-end gap-1">
                      <div className="w-full flex items-end gap-0.5 h-32">
                        <div className="w-1/3 bg-teal-500 h-[70%] rounded-t"></div>
                        <div className="w-1/3 bg-pink-500 h-[35%] rounded-t"></div>
                        <div className="w-1/3 bg-orange-400 h-[15%] rounded-t"></div>
                      </div>
                      <span className="text-[10px] text-slate-400">Apr</span>
                    </div>
                    {/* May */}
                    <div className="flex flex-col items-center flex-1 h-full justify-end gap-1">
                      <div className="w-full flex items-end gap-0.5 h-32">
                        <div className="w-1/3 bg-teal-500 h-[100%] rounded-t"></div>
                        <div className="w-1/3 bg-pink-500 h-[65%] rounded-t"></div>
                        <div className="w-1/3 bg-orange-400 h-[50%] rounded-t"></div>
                      </div>
                      <span className="text-[10px] text-slate-400">May</span>
                    </div>
                  </div>
                </div>

                {/* Stats Summary */}
                <div className="col-span-12 sm:col-span-4 space-y-2.5 text-xs">
                  <div className="p-2.5 bg-slate-50 rounded-xl border border-slate-100">
                    <p className="text-slate-400 text-[11px]">Warehouse stock</p>
                    <p className="text-lg font-bold text-slate-900">32,316</p>
                  </div>
                  <div className="p-2.5 bg-slate-50 rounded-xl border border-slate-100">
                    <p className="text-slate-400 text-[11px]">Low Stock Alerts</p>
                    <p className="text-lg font-bold text-amber-600">0</p>
                  </div>
                  <div className="p-2.5 bg-slate-50 rounded-xl border border-slate-100">
                    <p className="text-slate-400 text-[11px]">Deliveries Today</p>
                    <p className="text-lg font-bold text-teal-600">2</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Card 2: Overlapping Product Stock Details Card (Floating directly without any giant outer white background container!) */}
            <div className="absolute -bottom-6 right-2 sm:right-4 lg:-right-2 w-72 sm:w-80 bg-white rounded-2xl p-4 shadow-2xl border border-slate-100 text-slate-800 font-sans z-20 transition-transform duration-300 hover:scale-[1.02]">
              <h4 className="font-bold text-slate-900 text-xs sm:text-sm mb-2.5 pb-2 border-b border-slate-100">
                Product Stock Details
              </h4>

              <div className="space-y-2.5 text-xs">
                {/* Product Row 1 */}
                <div className="p-2 rounded-xl bg-slate-50 border border-slate-100 flex items-center gap-2.5">
                  <div className="w-9 h-9 bg-white rounded-lg p-1 flex items-center justify-center border border-slate-200 flex-shrink-0">
                    <span className="text-blue-600 font-bold text-[10px]">ACUVUE</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-slate-900 truncate text-[11px]">ACUVUE OASYS MAX (30-Pack)</p>
                    <div className="flex justify-between items-center text-[10px] text-slate-500 mt-0.5">
                      <span>On hand: <strong className="text-slate-800 font-semibold">5.00 Units</strong></span>
                      <span className="text-blue-600 font-bold">23.50 €</span>
                    </div>
                  </div>
                </div>

                {/* Product Row 2 */}
                <div className="p-2 rounded-xl bg-slate-50 border border-slate-100 flex items-center gap-2.5">
                  <div className="w-9 h-9 bg-white rounded-lg p-1 flex items-center justify-center border border-slate-200 flex-shrink-0">
                    <span className="text-teal-600 font-bold text-[9px]">B+L</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-slate-900 truncate text-[11px]">BAUSCH + LOMB ULTRA (6-Pack)</p>
                    <div className="flex justify-between items-center text-[10px] text-slate-500 mt-0.5">
                      <span>On hand: <strong className="text-slate-800 font-semibold">12.00 Units</strong></span>
                      <span className="text-blue-600 font-bold">19.99 €</span>
                    </div>
                  </div>
                </div>

                {/* Product Row 3 */}
                <div className="p-2 rounded-xl bg-slate-50 border border-slate-100 flex items-center gap-2.5">
                  <div className="w-9 h-9 bg-white rounded-lg p-1 flex items-center justify-center border border-slate-200 flex-shrink-0">
                    <span className="text-slate-800 font-bold text-[9px]">CARRERA</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-slate-900 truncate text-[11px]">CARRERA 8804 (Eyeglasses)</p>
                    <div className="flex justify-between items-center text-[10px] text-slate-500 mt-0.5">
                      <span>On hand: <strong className="text-slate-800 font-semibold">8.00 Units</strong></span>
                      <span className="text-blue-600 font-bold">145.00 €</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating Hand Truck Graphic Accent */}
            <div className="absolute -right-6 sm:-right-10 -bottom-10 w-16 sm:w-20 h-16 sm:h-20 text-amber-400 select-none pointer-events-none transform rotate-12 z-30">
              <svg viewBox="0 0 64 64" fill="none" className="w-full h-full">
                <path d="M12 8 L18 8 L24 48 L48 48" stroke="#f59e0b" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M10 12 L16 12" stroke="#f59e0b" strokeWidth="4" strokeLinecap="round" />
                <circle cx="26" cy="52" r="5" fill="#d97706" stroke="#f59e0b" strokeWidth="3" />
                <circle cx="44" cy="52" r="5" fill="#d97706" stroke="#f59e0b" strokeWidth="3" />
                <rect x="22" y="24" width="22" height="16" rx="2" fill="#fbbf24" stroke="#d97706" strokeWidth="2.5" />
                <line x1="33" y1="24" x2="33" y2="40" stroke="#d97706" strokeWidth="2" />
                <rect x="26" y="12" width="18" height="12" rx="2" fill="#f59e0b" stroke="#b45309" strokeWidth="2.5" />
                <line x1="35" y1="12" x2="35" y2="24" stroke="#b45309" strokeWidth="2" />
              </svg>
            </div>

          </motion.div>

        </div>
      </div>
    </section>
  );
}
